import { openDB } from 'idb';
import { getSupabase, getSession, getUserId } from './supabase-client.js';
import { normalizeBase, normalizeWishlistItem } from './instance-itinerary.js';
import {
  defaultChecklistItems,
  normalizeChecklistItems,
  resolveChecklistItems,
} from './utils/plan-checklist.js';
import { fetchWithTimeout } from './utils/network-fetch.js';
import { isReachable, markNetworkFailure, markNetworkSuccess } from './utils/connectivity.js';

const DB_NAME = 'metis';
const DB_VERSION = 2;
const STORE_NAME = 'snapshots';
const TRIP_BUNDLES_STORE = 'tripBundles';
const SHELF_CACHE_STORE = 'shelfCache';
const SHELF_CACHE_KEY = 'latest';
const NETWORK_TIMEOUT_MS = 6000;

export class OfflineError extends Error {
  constructor(message = 'Modifiche non disponibili offline') {
    super(message);
    this.name = 'OfflineError';
    this.code = 'OFFLINE';
  }
}

let dbPromise;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: 'instanceId' });
        }
        if (oldVersion < 2) {
          if (!database.objectStoreNames.contains(TRIP_BUNDLES_STORE)) {
            database.createObjectStore(TRIP_BUNDLES_STORE, { keyPath: 'tripId' });
          }
          if (!database.objectStoreNames.contains(SHELF_CACHE_STORE)) {
            database.createObjectStore(SHELF_CACHE_STORE, { keyPath: 'id' });
          }
        }
      },
    });
  }
  return dbPromise;
}

/** navigator.onLine + recent probe / fetch outcome (see connectivity.js). */
export function isOnline() {
  return isReachable();
}

let instanceEditorAccess = true;

export function setInstanceEditorAccess(value) {
  instanceEditorAccess = Boolean(value);
}

export function canEdit() {
  return isOnline() && instanceEditorAccess;
}

/** Editors and instance owner only — journal/viewers cannot see expenses. */
export function canViewExpenses() {
  return instanceEditorAccess;
}

/** Any trip member may write their own journal when online. */
export function canJournal() {
  return isOnline();
}

function assertCanEdit() {
  if (!canEdit()) {
    throw new OfflineError();
  }
}

async function requireUserId() {
  const session = await getSession();
  const userId = getUserId(session);
  if (!userId) {
    throw new Error('Sessione non valida');
  }
  return userId;
}

function groupStopsByDay(stops) {
  const map = new Map();
  for (const stop of stops ?? []) {
    const list = map.get(stop.day_id) ?? [];
    list.push(stop);
    map.set(stop.day_id, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.order_idx - b.order_idx);
  }
  return map;
}

function attachStopsToDays(days, stops) {
  const byDay = groupStopsByDay(stops);
  return (days ?? [])
    .slice()
    .sort((a, b) => a.order_idx - b.order_idx)
    .map((day) => ({
      ...day,
      stops: byDay.get(day.id) ?? [],
    }));
}

// ---------------------------------------------------------------------------
// Cache (IndexedDB)
// ---------------------------------------------------------------------------

export async function cacheSnapshot(instanceId, data) {
  const db = await getDb();
  await db.put(STORE_NAME, {
    instanceId,
    cachedAt: new Date().toISOString(),
    ...data,
  });
}

export async function getCachedSnapshot(instanceId) {
  const db = await getDb();
  return db.get(STORE_NAME, instanceId);
}

export async function cacheTripBundle(tripId, bundle) {
  const db = await getDb();
  await db.put(TRIP_BUNDLES_STORE, {
    tripId,
    cachedAt: new Date().toISOString(),
    ...bundle,
  });
}

export async function getCachedTripBundle(tripId) {
  const db = await getDb();
  return db.get(TRIP_BUNDLES_STORE, tripId);
}

export async function listCachedTripIds() {
  const db = await getDb();
  const keys = await db.getAllKeys(TRIP_BUNDLES_STORE);
  return keys;
}

export async function cacheShelf(shelf) {
  const db = await getDb();
  await db.put(SHELF_CACHE_STORE, {
    id: SHELF_CACHE_KEY,
    cachedAt: new Date().toISOString(),
    shelf,
  });
}

/** Keep shelf cache in sync when a trip is downloaded for offline use. */
export async function upsertTripOnCachedShelf(trip, plan, instanceId) {
  const entry = await getCachedShelf();
  const shelf = entry?.shelf ? [...entry.shelf] : [];
  const idx = shelf.findIndex((row) => row.trip.id === trip.id);
  const row = { trip, plan, instanceId };
  if (idx >= 0) shelf[idx] = row;
  else shelf.push(row);
  await cacheShelf(shelf);
}

export async function getCachedShelf() {
  const db = await getDb();
  return db.get(SHELF_CACHE_STORE, SHELF_CACHE_KEY);
}

function snapshotToLiveData(cached) {
  return {
    checks: cached.checks ?? [],
    notes: cached.notes ?? [],
    expenses: cached.expenses ?? [],
    journal: cached.journal ?? [],
    members: cached.members ?? [],
    bases: cached.bases ?? [],
    wishlist: cached.wishlist ?? [],
    fromCache: true,
  };
}

async function fetchInstanceDataFromNetwork(instanceId) {
  const supabase = getSupabase();

  const [checksRes, notesRes, expensesRes, journalRes, membersRes, basesRes, wishlistRes] =
    await Promise.all([
      supabase.from('checks').select('*').eq('instance_id', instanceId).order('item_key'),
      supabase.from('notes').select('*').eq('instance_id', instanceId).order('day_key'),
      supabase.from('expenses').select('*').eq('instance_id', instanceId).order('updated_at'),
      supabase.from('journal').select('*').eq('instance_id', instanceId).order('day_key'),
      supabase.from('instance_members').select('*').eq('instance_id', instanceId),
      supabase.from('instance_bases').select('*').eq('instance_id', instanceId).order('order_idx'),
      supabase.from('instance_wishlist').select('*').eq('instance_id', instanceId).order('order_idx'),
    ]);

  const firstError =
    checksRes.error ??
    notesRes.error ??
    expensesRes.error ??
    journalRes.error ??
    membersRes.error ??
    basesRes.error ??
    wishlistRes.error;
  if (firstError) throw firstError;

  return {
    checks: checksRes.data ?? [],
    notes: notesRes.data ?? [],
    expenses: expensesRes.data ?? [],
    journal: journalRes.data ?? [],
    members: membersRes.data ?? [],
    bases: (basesRes.data ?? []).map(normalizeBase),
    wishlist: (wishlistRes.data ?? []).map(normalizeWishlistItem),
    fromCache: false,
  };
}

// ---------------------------------------------------------------------------
// Piano
// ---------------------------------------------------------------------------

export async function loadTrip(tripId) {
  const supabase = getSupabase();

  const { data: trip, error: tripError } = await supabase
    .from('trips')
    .select('*')
    .eq('id', tripId)
    .single();
  if (tripError) throw tripError;

  const { data: plan, error: planError } = await supabase
    .from('plans')
    .select('*')
    .eq('trip_id', tripId)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (planError) throw planError;
  if (!plan) throw new Error('Piano non trovato');

  const { data: days, error: daysError } = await supabase
    .from('plan_days')
    .select('*')
    .eq('plan_id', plan.id)
    .order('order_idx');
  if (daysError) throw daysError;

  const dayIds = (days ?? []).map((d) => d.id);
  let stops = [];
  if (dayIds.length) {
    const { data: stopRows, error: stopsError } = await supabase
      .from('plan_stops')
      .select('*')
      .in('day_id', dayIds)
      .order('order_idx');
    if (stopsError) throw stopsError;
    stops = stopRows ?? [];
  }

  return {
    trip,
    plan,
    days: attachStopsToDays(days, stops),
    sections: plan.sections ?? {},
  };
}

export async function loadShelf() {
  const supabase = getSupabase();
  const { data: trips, error } = await supabase
    .from('trips')
    .select('*, plans(*)')
    .order('date_start', { ascending: true });
  if (error) throw error;

  return (trips ?? []).map((trip) => {
    const plans = trip.plans ?? [];
    const plan = plans.slice().sort((a, b) => b.version - a.version)[0] ?? null;
    const { plans: _plans, ...tripRow } = trip;
    return { trip: tripRow, plan };
  });
}

export async function loadShelfWithInstances() {
  const shelf = await loadShelf();
  const userId = await requireUserId();
  const supabase = getSupabase();

  const { data: memberships, error } = await supabase
    .from('instance_members')
    .select('instance_id, instances!inner(id, plan_id)')
    .eq('user_id', userId);
  if (error) throw error;

  const instanceByPlanId = new Map();
  for (const row of memberships ?? []) {
    const inst = row.instances;
    if (inst?.plan_id && !instanceByPlanId.has(inst.plan_id)) {
      instanceByPlanId.set(inst.plan_id, inst.id);
    }
  }

  return shelf.map((entry) => ({
    ...entry,
    instanceId: entry.plan ? instanceByPlanId.get(entry.plan.id) ?? null : null,
  }));
}

// ---------------------------------------------------------------------------
// Piano — creazione wizard
// ---------------------------------------------------------------------------

export async function createTripDraft({ title, country, dateStart, dateEnd, party = {} }) {
  const supabase = getSupabase();
  const userId = await requireUserId();

  const { data: trip, error: tripError } = await supabase
    .from('trips')
    .insert({
      title,
      country,
      date_start: dateStart,
      date_end: dateEnd,
      party,
      owner_id: userId,
      visibility: 'private',
    })
    .select()
    .single();
  if (tripError) throw tripError;

  const { error: collabError } = await supabase.from('collaborators').insert({
    trip_id: trip.id,
    user_id: userId,
    role: 'owner',
  });
  if (collabError) throw collabError;

  const { data: plan, error: planError } = await supabase
    .from('plans')
    .insert({
      trip_id: trip.id,
      version: 1,
      status: 'draft',
      sections: {},
      checklist_items: [],
    })
    .select()
    .single();
  if (planError) throw planError;

  return { trip, plan };
}

export async function loadPlanDraft(tripId) {
  const tripData = await loadTrip(tripId);
  if (tripData.plan.status !== 'draft') {
    throw new Error('Questa rotta è già pubblicata');
  }
  return tripData;
}

export async function updateTripMeta(tripId, { title, country, dateStart, dateEnd }) {
  assertCanEdit();
  const supabase = getSupabase();
  const patch = {};
  if (title !== undefined) patch.title = title;
  if (country !== undefined) patch.country = country;
  if (dateStart !== undefined) patch.date_start = dateStart;
  if (dateEnd !== undefined) patch.date_end = dateEnd;

  const { data, error } = await supabase.from('trips').update(patch).eq('id', tripId).select().single();
  if (error) throw error;
  return data;
}

export async function replacePlanDays(planId, days) {
  assertCanEdit();
  const supabase = getSupabase();

  const { data: existingDays, error: listError } = await supabase
    .from('plan_days')
    .select('id')
    .eq('plan_id', planId);
  if (listError) throw listError;

  const existingIds = (existingDays ?? []).map((d) => d.id);
  if (existingIds.length) {
    const { error: delStopsError } = await supabase.from('plan_stops').delete().in('day_id', existingIds);
    if (delStopsError) throw delStopsError;
    const { error: delDaysError } = await supabase.from('plan_days').delete().eq('plan_id', planId);
    if (delDaysError) throw delDaysError;
  }

  const insertedDays = [];
  for (let i = 0; i < days.length; i++) {
    const day = days[i];
    const { data: row, error } = await supabase
      .from('plan_days')
      .insert({
        plan_id: planId,
        date: day.date,
        day_type: day.day_type ?? day.dayType ?? 'nature',
        title: day.title,
        ritmo: day.ritmo ?? null,
        order_idx: day.order_idx ?? day.orderIdx ?? i,
      })
      .select()
      .single();
    if (error) throw error;

    const stops = day.stops ?? [];
    for (let j = 0; j < stops.length; j++) {
      const stop = stops[j];
      const { error: stopError } = await supabase.from('plan_stops').insert({
        day_id: row.id,
        name: stop.name,
        car_time: stop.car_time ?? stop.carTime ?? null,
        walk_info: stop.walk_info ?? stop.walkInfo ?? null,
        cost_info: stop.cost_info ?? stop.costInfo ?? null,
        maps_url: stop.maps_url ?? stop.mapsUrl ?? null,
        warning: stop.warning ?? null,
        description: stop.description ?? null,
        order_idx: stop.order_idx ?? stop.orderIdx ?? j,
      });
      if (stopError) throw stopError;
    }
    insertedDays.push(row);
  }

  return insertedDays;
}

export async function updatePlanChecklist(planId, checklistItems) {
  assertCanEdit();
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('plans')
    .update({ checklist_items: normalizeChecklistItems(checklistItems) })
    .eq('id', planId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateInstanceChecklist(instanceId, checklistItems) {
  assertCanEdit();
  const supabase = getSupabase();
  const normalized = normalizeChecklistItems(checklistItems);
  const { data, error } = await supabase
    .from('instances')
    .update({ checklist_items: normalized })
    .eq('id', instanceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Seed instance checklist from plan defaults on first Lista open. */
export async function ensureInstanceChecklist(instance, plan) {
  const existing = instance?.checklist_items ?? instance?.checklistItems;
  if (Array.isArray(existing) && existing.length > 0) {
    return normalizeChecklistItems(existing);
  }
  const defaults = defaultChecklistItems(plan);
  if (defaults.length === 0) return [];
  if (!isOnline()) return defaults;
  const updated = await updateInstanceChecklist(instance.id, defaults);
  return normalizeChecklistItems(updated.checklist_items);
}

export async function removeChecklistItem(instanceId, itemKey, checklistItems) {
  assertCanEdit();
  const next = normalizeChecklistItems(checklistItems).filter((item) => item.itemKey !== itemKey);
  const supabase = getSupabase();

  const { error: checkError } = await supabase
    .from('checks')
    .delete()
    .eq('instance_id', instanceId)
    .eq('item_key', itemKey);
  if (checkError) throw checkError;

  return updateInstanceChecklist(instanceId, next);
}

export async function publishPlan(planId) {
  assertCanEdit();
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('plans')
    .update({ status: 'published' })
    .eq('id', planId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function findMemberInstanceForPlan(planId) {
  const userId = await requireUserId();
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('instance_members')
    .select('instances!inner(id, plan_id, owner_id, started_at, checklist_items)')
    .eq('user_id', userId)
    .eq('instances.plan_id', planId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.instances ?? null;
}

export async function createInstanceFromWizard({
  planId,
  displayName,
  avatarEmoji = '👤',
  bases = [],
}) {
  assertCanEdit();
  const supabase = getSupabase();
  const userId = await requireUserId();

  const existing = await findMemberInstanceForPlan(planId);
  if (existing) return existing;

  const { data: instance, error: instError } = await supabase
    .from('instances')
    .insert({ plan_id: planId, owner_id: userId })
    .select()
    .single();
  if (instError) throw instError;

  const { error: memberError } = await supabase.from('instance_members').insert({
    instance_id: instance.id,
    user_id: userId,
    display_name: displayName,
    avatar_emoji: avatarEmoji,
    role: 'editor',
  });
  if (memberError) throw memberError;

  for (let i = 0; i < bases.length; i++) {
    const base = bases[i];
    if (!base.name?.trim()) continue;
    const { error: baseError } = await supabase.from('instance_bases').insert({
      instance_id: instance.id,
      name: base.name.trim(),
      subtitle: base.subtitle?.trim() || null,
      date_start: base.dateStart,
      date_end: base.dateEnd,
      maps_url: base.mapsUrl?.trim() || null,
      order_idx: i,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    });
    if (baseError) throw baseError;
  }

  return instance;
}

export { resolveChecklistItems, normalizeChecklistItems, defaultChecklistItems };

// ---------------------------------------------------------------------------
// Istanza
// ---------------------------------------------------------------------------

export async function removeInstanceMember(instanceId, userId) {
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_members')
    .delete()
    .eq('instance_id', instanceId)
    .eq('user_id', userId);
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { members: live.members });
}

export async function updateMyInstanceProfile(instanceId, { displayName, avatarEmoji }) {
  if (!isOnline()) throw new OfflineError();
  const supabase = getSupabase();
  const { error } = await supabase.rpc('update_my_instance_profile', {
    p_instance_id: instanceId,
    p_display_name: displayName,
    p_avatar_emoji: avatarEmoji,
  });
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { members: live.members });
}

export async function setInstanceMemberRole(instanceId, userId, role) {
  if (!isOnline()) throw new OfflineError();
  const supabase = getSupabase();
  const { error } = await supabase.rpc('set_instance_member_role', {
    p_instance_id: instanceId,
    p_user_id: userId,
    p_role: role,
  });
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { members: live.members });
}

export async function fetchInstance(instanceId) {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('instances')
    .select('id, plan_id, owner_id, started_at, checklist_items')
    .eq('id', instanceId)
    .single();
  if (error) throw error;
  return data;
}

export async function getOrCreateFamilyInstance(planId) {
  const supabase = getSupabase();
  const userId = await requireUserId();

  const { data: existing, error: findError } = await supabase
    .from('instances')
    .select('*')
    .eq('plan_id', planId)
    .order('started_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (findError) throw findError;
  if (existing) return existing;

  const { data: created, error: createError } = await supabase
    .from('instances')
    .insert({ plan_id: planId, owner_id: userId })
    .select()
    .single();
  if (createError) throw createError;

  const { error: memberError } = await supabase.from('instance_members').insert({
    instance_id: created.id,
    user_id: userId,
    display_name: 'Viaggiatore',
    avatar_emoji: '👤',
    role: 'editor',
  });
  if (memberError) throw memberError;

  return created;
}

export async function loadInstanceData(instanceId, { forceNetwork = false, networkTimeoutMs = NETWORK_TIMEOUT_MS } = {}) {
  const cached = await getCachedSnapshot(instanceId);

  if (!isOnline()) {
    if (cached) return snapshotToLiveData(cached);
    throw new OfflineError('Nessun dato in cache per questa istanza');
  }

  if (!forceNetwork && cached) {
    fetchWithTimeout(fetchInstanceDataFromNetwork(instanceId), networkTimeoutMs)
      .then(async (payload) => {
        await cacheSnapshot(instanceId, payload);
      })
      .catch(() => {});
    return snapshotToLiveData(cached);
  }

  try {
    const payload = await fetchWithTimeout(
      fetchInstanceDataFromNetwork(instanceId),
      networkTimeoutMs,
    );
    await cacheSnapshot(instanceId, payload);
    return payload;
  } catch (err) {
    if (cached) return { ...snapshotToLiveData(cached), stale: true };
    throw err;
  }
}

// ---------------------------------------------------------------------------
// CRUD — dati vivi
// ---------------------------------------------------------------------------

async function refreshCachePartial(instanceId, patch) {
  const cached = (await getCachedSnapshot(instanceId)) ?? { instanceId };
  await cacheSnapshot(instanceId, { ...cached, ...patch });
}

export async function toggleCheck(instanceId, itemKey, checked) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('checks')
    .upsert(
      {
        instance_id: instanceId,
        item_key: itemKey,
        checked,
        updated_by: userId,
        updated_at: now,
      },
      { onConflict: 'instance_id,item_key' },
    )
    .select()
    .single();
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { checks: live.checks });
  return data;
}

export async function upsertNote(instanceId, dayKey, body) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('notes')
    .upsert(
      {
        instance_id: instanceId,
        day_key: dayKey,
        body,
        updated_by: userId,
        updated_at: now,
      },
      { onConflict: 'instance_id,day_key' },
    )
    .select()
    .single();
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { notes: live.notes });
  return data;
}

export async function addExpense(instanceId, dayKey, label, amountEur, category = 'altro') {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('expenses')
    .insert({
      instance_id: instanceId,
      day_key: dayKey,
      label,
      amount_eur: amountEur,
      category,
      updated_by: userId,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { expenses: live.expenses });
  return data;
}

export async function updateExpense(id, { label, amountEur, dayKey, category }) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const patch = { updated_by: userId, updated_at: now };
  if (label !== undefined) patch.label = label;
  if (amountEur !== undefined) patch.amount_eur = amountEur;
  if (dayKey !== undefined) patch.day_key = dayKey;
  if (category !== undefined) patch.category = category;

  const { data, error } = await supabase.from('expenses').update(patch).eq('id', id).select().single();
  if (error) throw error;

  const live = await loadInstanceData(data.instance_id, { forceNetwork: true });
  await refreshCachePartial(data.instance_id, { expenses: live.expenses });
  return data;
}

export async function deleteExpense(id, instanceId) {
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase.from('expenses').delete().eq('id', id);
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { expenses: live.expenses });
}

export async function upsertJournal(instanceId, dayKey, highlight, rating) {
  if (!canJournal()) {
    throw new OfflineError();
  }
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('journal')
    .upsert(
      {
        instance_id: instanceId,
        day_key: dayKey,
        user_id: userId,
        highlight,
        rating,
        updated_by: userId,
        updated_at: now,
      },
      { onConflict: 'instance_id,day_key,user_id' },
    )
    .select()
    .single();
  if (error) throw error;

  const live = await loadInstanceData(instanceId, { forceNetwork: true });
  await refreshCachePartial(instanceId, { journal: live.journal });
  return data;
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

export function subscribeInstance(instanceId, onChange) {
  const supabase = getSupabase();
  const tables = [
    'checks',
    'notes',
    'expenses',
    'journal',
    'instance_members',
    'instance_bases',
    'instance_days',
    'instance_stops',
  ];

  const channel = supabase
    .channel(`instance:${instanceId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instances', filter: `id=eq.${instanceId}` },
      () => onChange({ table: 'instances' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'checks', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'checks' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'notes', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'notes' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'expenses', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'expenses' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'journal', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'journal' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instance_members', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'instance_members' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instance_bases', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'instance_bases' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instance_days', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'instance_days' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instance_stops', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'instance_stops' }),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'instance_wishlist', filter: `instance_id=eq.${instanceId}` },
      () => onChange({ table: 'instance_wishlist' }),
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
